import realestates from '@/api/realestates'
import deleteRecord from '@/utils/delete-record'
import findIndex from 'lodash/findIndex'
import Vue from 'vue'

const getBaseState = () => ({
  realEstates: [],
  histories: {},
  completeHistory: {}
})

export default () => ({
  namespaced: true,

  state: getBaseState(),

  actions: {
    fetchCompleteHistory (context, cached = true) {
      if (cached && context.state.completeHistory.data) {
        return Promise.resolve()
      }

      context.state.realEstates.forEach(realestate => {
        realestates.fetchCompleteHistory(realestate.id)
          .then(data => context.commit('fetchCompleteHistory', {data, id: realestate.id}))
      })
    },

    fetchHistory (context, { contractId, from, cached = true }) {
      if (cached && context.state.histories[contractId]) {
        return Promise.resolve(context.state.histories[contractId])
      }
      return realestates.fetchHistory(contractId, from)
        .then(history => {
          context.commit('fetchHistory', { contractId, history })
          return history
        })
    },

    updateInfos (context, data) {
      const infos = data.infos
      const id = data.id

      return realestates.updateRealEstate(id, infos)
        .then(() => context.dispatch('fetchRealEstateById', {id, cached: false}))
    },

    updateName (context, data) {
      return realestates.update(data.id, data)
        .then(() => context.dispatch('fetchRealEstateById', {id: data.id, cached: false}))
    },

    add (context, data) {
      return realestates.add(data)
        .then(({realEstate}) => {
          return realestates.estimate(realEstate.id)
            .then(({estimate}) => {
              realEstate.estimate = estimate
              context.commit('add', realEstate)
              return realEstate
            })
        })
    },

    addMan (context, data) {
      return realestates.add(data)
        .then(({realEstate}) => {
          return realestates.estimateMan(realEstate.id, {assetPrice: data.currentAmount})
            .then(({estimate}) => {
              realEstate.estimate = estimate
              realEstate.amount = estimate.history[estimate.history.length - 1][1]
              context.commit('add', realEstate)
              return realEstate
            })
        })
    },

    updateAssetPrice (context, data) {
      return realestates.estimateMan(data.id, {assetPrice: data.assetPrice})
        .then(({estimate}) => {
          context.commit('addEstimate', {estimate: estimate, id: data.id})
        })
    },

    fetch (context, cached = true) {
      const { realEstates } = context.state
      if (cached && realEstates.length) {
        return Promise.resolve(realEstates)
      }

      return realestates.fetch()
        .then(({realEstates}) => {
          realEstates.forEach(realEstate => {
            context.commit('add', realEstate)
          })
        })
    },

    fetchByUser (context, { id, cached = true }) {
      const { realEstates } = context.state
      if (cached && realEstates.length) {
        return Promise.resolve(realEstates)
      }

      return realestates.fetchRealEstatesByUser(id)
        .then(({realEstates}) => {
          realEstates.forEach(realEstate => {
            context.commit('add', realEstate)
          })
        })
    },

    fetchRealEstateById (context, { id, cached = true }) {
      let realEstate
      if (cached && (realEstate = context.getters.getById(id))) {
        return Promise.resolve(realEstate)
      }

      return realestates.fetchById(id)
        .then(({realEstate}) => {
          return realestates.fetchEstimate(id)
            .then(({estimate}) => {
              realEstate.estimate = estimate
              context.commit('add', realEstate)
              return realEstate
            })
        })
    },

    fetchEstimate (context, { id, cached = true }) {
      let { estimate } = context.getters.getById(id)
      if (cached && estimate) {
        return Promise.resolve('Estimate already cached')
      }

      return realestates.fetchEstimate(id)
        .then(({estimate}) => {
          context.commit('addEstimate', {estimate: estimate, id: id})
        })
    },

    update (context, data) {
      const { id, infos } = data

      return realestates.updateRealEstate(id, infos)
        .then(() => context.dispatch('fetchRealEstateById', {id, cached: false}))
    },

    delete (context, id) {
      return realestates.delete(id)
        .then(() => {
          context.commit('delete', id)
        })
    },

    searchAddress (context, address) {
      return realestates.fetchAddress(address)
        .then(({ data }) => data.features)
    }
  },

  mutations: {
    add (state, data) {
      const index = findIndex(state.realEstates, { id: data.id })
      if (index >= 0) {
        state.realEstates = [
          ...state.realEstates.slice(0, index),
          data,
          ...state.realEstates.slice(index + 1)
        ]
      } else {
        state.realEstates = [
          ...state.realEstates,
          data
        ]
      }
    },

    addEstimate (state, data) {
      const index = findIndex(state.realEstates, { id: data.id })
      Vue.set(state.realEstates[index], 'estimate', data.estimate)
    },

    delete ({ realEstates }, id) {
      deleteRecord(realEstates, id)
    },

    fetchCompleteHistory (state, { data, id }) {
      Vue.set(state.completeHistory, id, data.data)
    },

    fetchHistory (state, { contractId, history }) {
      state.histories = {
        ...state.histories,
        [contractId]: history
      }
    },

    reset (state) {
      const baseState = getBaseState()
      Object.keys(baseState).forEach(key => {
        state[key] = baseState[key]
      })
    }
  },

  getters: {
    getById: ({ realEstates }) => (id) => {
      return realEstates.find(realEstate => realEstate.id === id)
    },

    getGeocode: ({ realEstates }) => (id) => {
      let realEstate = realEstates.find(realEstate => realEstate.id === id)
      return realEstate.location
    },

    getHistoryById: ({ histories }) => (id) => histories[id],

    getFirstDate: ({ realEstates }) => (id) => {
      const { history } = realEstates.find(realEstate => realEstate.id === id).estimate
      return history[0] ? history[0][0] : Date.now()
    },

    getCumulativeValues: ({ realEstates }) => (key) => {
      return realEstates.reduce((sum, {[key]: value}) => sum + value, 0)
    }
  }
})
